import { TFilterField, TFilterValue } from "@irohana-dev/components";
import { TOption } from "@/types/common";
import { applyColumnSettings as applyKitColumnSettings, asStringArray, buildColumnOptions, fromDateRangeValue } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import {
  partnerContactPersonSearchConfig,
  partnerLocationSearchConfig,
  partnerManagementNumberSearchConfig,
  partnerNameFieldSearchConfig,
} from "@/api/services/main/partner";
import {
  PARTNER_DATE_RANGE_KEY,
  PARTNER_MULTI_SELECT_FILTER_KEYS,
  TPartnerDisplayColumnSetting,
  TPartnerFilterFormExtended,
  defaultPartnerDisplayColumns,
  defaultPartnerFilterValues,
} from "@/features/partner/components/partner-list/partner-filter-shared";

type TPartnerFilterOptions = {
  nationality: TOption[];
  prefecture: TOption[];
};

export type TPartnerAsyncFieldEnabled = {
  managementNumber: boolean;
  partnerName: boolean;
  location: boolean;
  contactPerson: boolean;
};

export const buildPartnerFilterFields = (
  options: TPartnerFilterOptions,
  cityOptions: TOption[],
  hasPrefecture: boolean,
  isOverseas: boolean,
  enabled: TPartnerAsyncFieldEnabled,
): TFilterField[] => {
  const fields: TFilterField[] = [
    {
      type: "asyncMultiSelect",
      key: "managementNumber",
      label: "パートナー管理番号",
      searchConfig: partnerManagementNumberSearchConfig,
      enabled: enabled.managementNumber,
    },
    { type: "asyncMultiSelect", key: "partnerName", label: "パートナー名", searchConfig: partnerNameFieldSearchConfig, enabled: enabled.partnerName },
    { type: "multiSelect", key: "nationalityId", label: "国籍", options: options.nationality },
    { type: "multiSelect", key: "prefecture", label: "所在地（都道府県）", options: options.prefecture },
    { type: "multiSelect", key: "city", label: "所在地（市区町村）", options: cityOptions, disabled: !hasPrefecture },
    {
      type: "asyncMultiSelect",
      key: "contactPerson",
      label: "担当者",
      searchConfig: partnerContactPersonSearchConfig,
      enabled: enabled.contactPerson,
    },
    { type: "dateRange", key: PARTNER_DATE_RANGE_KEY, label: "登録日" },
    { type: "checkbox", key: "isOverseas", label: "所在地が国外" },
  ];
  if (isOverseas) {
    fields.push({
      type: "asyncMultiSelect",
      key: "location",
      label: "所在地",
      searchConfig: partnerLocationSearchConfig,
      enabled: enabled.location,
    });
  }
  return fields;
};

export const buildPartnerColumnOptions = buildColumnOptions;

export const toFilterValue = (filters: TPartnerFilterFormExtended): TFilterValue => {
  const value: TFilterValue = {};
  for (const [key, raw] of Object.entries(filters) as [string, unknown][]) {
    if (key === "registrationDateFrom" || key === "registrationDateTo") continue;
    if (key === "isOverseas") {
      if (raw) value[key] = true;
      continue;
    }
    if (Array.isArray(raw)) {
      if (raw.length > 0) value[key] = raw.map(String);
      continue;
    }
    if (raw === "" || raw === null || raw === undefined) continue;
    if (PARTNER_MULTI_SELECT_FILTER_KEYS.includes(key as keyof TPartnerFilterFormExtended)) {
      value[key] = [String(raw)];
    }
  }
  const { registrationDateFrom, registrationDateTo } = filters;
  if (registrationDateFrom || registrationDateTo) {
    value[PARTNER_DATE_RANGE_KEY] = { from: registrationDateFrom, to: registrationDateTo };
  }
  return value;
};

export const fromFilterValue = (value: TFilterValue): TPartnerFilterFormExtended => {
  const range = fromDateRangeValue(value, PARTNER_DATE_RANGE_KEY);
  const isOverseas = value.isOverseas === true;
  return {
    ...defaultPartnerFilterValues,
    managementNumber: asStringArray(value.managementNumber),
    partnerName: asStringArray(value.partnerName),
    nationalityId: asStringArray(value.nationalityId),
    prefecture: asStringArray(value.prefecture),
    city: asStringArray(value.city),
    isOverseas,
    location: isOverseas ? asStringArray(value.location) : [],
    contactPerson: asStringArray(value.contactPerson),
    registrationDateFrom: range.from,
    registrationDateTo: range.to,
  };
};

export const applyColumnSettings = (columns: TPartnerDisplayColumnSetting[], order: string[], hidden: string[]): TPartnerDisplayColumnSetting[] =>
  applyKitColumnSettings(columns, order, hidden, defaultPartnerDisplayColumns);
